import {
    Avatar,
    ButtonGradientPrimary,
    Checkbox,
    htmlToSlate,
    Icon,
    RichText,
    slateToHtml,
} from '@amelio/ui-library';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ObjectiveHelpTooltip from '../ObjectiveHelpTooltip';

import { useGetUserById } from '@/api/directory';
import { useCreateObjectiveComment } from '@/api/objectives';
import { EDITOR_TOOLBAR_DENSITY } from '@/constants/RichTextEditor';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';
import { useError } from '@/hooks/utils/useError';
import { useLocalStorage } from '@/hooks/utils/useLocalStorage';
import i18n from '@/plugins/i18n';
import { showSuccessToast } from '@/plugins/Toast';
import { htmlToPlainText } from '@/utils/htmlSanitizer';
import { getFullName, getInitials, getProfilePictureUrl } from '@/utils/user';

/**
 * Where the writer's last answer to "notify the contributors" is remembered. One key for the whole
 * application, not one per objective: the choice says how this person likes to work, not something
 * about a particular objective. It lives in this browser only — on another one the checkbox comes
 * back ticked, which is what a first-time writer gets anyway.
 */
const NOTIFY_COLLABORATORS_STORAGE_KEY = 'objectives.discussion.notifyCollaborators';

interface ObjectiveDiscussionComposerProps {
    objectiveId: string;
}

/**
 * Writes one message into an objective's discussion: the writer's avatar, the rich-text editor, the
 * choice of telling the other collaborators, and the publish button. The checkbox and the message
 * travel in the same request, so what the screen shows is what the server does.
 */
export default function ObjectiveDiscussionComposer({
    objectiveId,
}: ObjectiveDiscussionComposerProps) {
    const { t } = useTranslation();
    const { handleError } = useError();
    const { getManagerLabel } = useCompanyInformation();
    const managerLabel = getManagerLabel();

    const { data: currentUser } = useGetUserById({ userId: 'me' });
    const createComment = useCreateObjectiveComment();

    const { value: notifyCollaborators, setValue: setNotifyCollaborators } = useLocalStorage({
        key: NOTIFY_COLLABORATORS_STORAGE_KEY,
        initialValue: true,
    });

    const [commentHtml, setCommentHtml] = useState('');
    // Seeded as `defaultValue`: `RichText` counts a given `value`, not the typing. Nothing clears
    // the editor after a publish, so its `key` remounts it.
    const [editorResetKey, setEditorResetKey] = useState(0);
    const emptyCommentValue = useMemo(() => htmlToSlate(''), []);

    const isBodyEmpty = htmlToPlainText(commentHtml).trim() === '';

    const handlePublish = async () => {
        if (isBodyEmpty || createComment.isPending) {
            return;
        }

        try {
            await createComment.mutateAsync({
                objectiveId,
                request: { body: commentHtml, notifyCollaborators },
            });
            showSuccessToast({ message: t('objectives.detail.discussion.published') });
            setCommentHtml('');
            setEditorResetKey((key) => key + 1);
        } catch (error) {
            handleError({ error });
        }
    };

    return (
        // The avatar owns the left column; everything the writer acts on -- the editor, then the
        // notice choice and the publish button -- lives in the column beside it. The indent is the
        // avatar's own width, so the checkbox starts exactly where the typed text starts and no
        // number repeats it.
        <div className={'flex items-start gap-2'}>
            <div className={'obj-discussion-avatar shrink-0'}>
                <Avatar
                    fill
                    src={
                        currentUser
                            ? getProfilePictureUrl(currentUser.profilePictureUrl)
                            : undefined
                    }
                    alt={
                        currentUser
                            ? getFullName(currentUser)
                            : t('objectives.detail.discussion.unknownAuthor')
                    }
                    initials={currentUser ? getInitials(currentUser) : undefined}
                    className={'text-[12px] md:text-[14px]'}
                    bgColor={'gradient'}
                />
            </div>

            <div
                className={'obj-discussion-composer-gap flex flex-1 flex-col'}
                data-testid={'objective-discussion-composer-column'}
            >
                <RichText
                    key={editorResetKey}
                    toolbarDensity={EDITOR_TOOLBAR_DENSITY}
                    defaultValue={emptyCommentValue}
                    onChange={(value) => setCommentHtml(slateToHtml(value))}
                    placeholder={t('objectives.detail.discussion.placeholder')}
                    maxLength={5000}
                    showCharacterCount
                    // A short translucent box, growing with the lines typed: a message here is
                    // usually one or two sentences, and the editor sits inside the glass panel.
                    surface={'glass'}
                    contentMinHeight={'compact'}
                    locale={i18n.language}
                    dataTestId={'objective-discussion-editor'}
                />

                <div className={'flex flex-wrap items-center justify-between gap-3'}>
                    <div className={'flex items-center gap-2'}>
                        <Checkbox
                            size={'sm'}
                            checked={notifyCollaborators}
                            onChange={setNotifyCollaborators}
                            label={t('objectives.detail.discussion.notifyCollaborators', {
                                managerLabel,
                            })}
                            dataTestId={'objective-discussion-notify'}
                        />

                        <ObjectiveHelpTooltip
                            label={t('objectives.detail.discussion.notifyHelp.label')}
                            dataTestId={'objective-discussion-notify-help'}
                        >
                            {t('objectives.detail.discussion.notifyHelp.body', { managerLabel })}
                        </ObjectiveHelpTooltip>
                    </div>

                    <ButtonGradientPrimary
                        size={'sm'}
                        fitContent
                        leftIcon={
                            <Icon
                                name={'essential-send'}
                                size={'lg'}
                            />
                        }
                        loading={createComment.isPending}
                        disabled={isBodyEmpty || createComment.isPending}
                        onClick={handlePublish}
                        dataTestId={'objective-discussion-publish'}
                    >
                        {t('objectives.detail.discussion.publish')}
                    </ButtonGradientPrimary>
                </div>
            </div>
        </div>
    );
}
